// Centralized Feature Flags Service
// Maps subscription plans to feature permissions

export interface FeatureFlags {
  // Limits
  aiAnalysesLimit: number | null; // null = unlimited
  
  // Core Features
  canUseAiAnalysis: boolean;
  canUseJournal: boolean;
  
  // NO_TRADE Logic
  canSeeBasicNoTrade: boolean;
  hasStructuredNoTradeLogic: boolean;
  
  // Tracking & Metrics
  hasCounterfactualTracking: boolean;
  hasDecisionQualityScore: boolean;
  hasRiskAvoidanceScore: boolean;
  hasCapitalPreservationPercent: boolean;
  hasImpulsiveTraderBenchmark: boolean;
  
  // TDI Score
  hasTdiScore: boolean;
  tdiVersion: 'none' | 'base' | 'premium_850';
  hasTdiHistoryChart: boolean;
  hasBehavioralDriftDetection: boolean;
  hasOvertradingClusterAnalysis: boolean;
  
  // Institutional Features
  hasInstitutionalReviewBlock: boolean;
  hasRiskOfficerStance: boolean;
  hasCapitalProtectionScore: boolean;
  hasMarketAlignmentScore: boolean;
  hasDisciplineAssessment: boolean;
  hasFullCounterfactualProofEngine: boolean;
  
  // Reporting
  hasAuditPdf: boolean;
  hasPmReadyReporting: boolean;
  
  // Enterprise Features
  hasComplianceFeatures: boolean;
  hasAuditApi: boolean;
  hasMultiUserDashboards: boolean;
  hasDisciplineReportsDailyWeekly: boolean;
  hasWhiteLabelAi: boolean;
  hasCustomRiskOfficerProfiles: boolean;
  hasPrioritySupport: boolean;
}

// Default flags (no plan / free)
const noAccessFlags: FeatureFlags = {
  aiAnalysesLimit: 0,
  canUseAiAnalysis: false,
  canUseJournal: false,
  canSeeBasicNoTrade: false,
  hasStructuredNoTradeLogic: false,
  hasCounterfactualTracking: false,
  hasDecisionQualityScore: false,
  hasRiskAvoidanceScore: false,
  hasCapitalPreservationPercent: false,
  hasImpulsiveTraderBenchmark: false,
  hasTdiScore: false,
  tdiVersion: 'none',
  hasTdiHistoryChart: false,
  hasBehavioralDriftDetection: false,
  hasOvertradingClusterAnalysis: false,
  hasInstitutionalReviewBlock: false,
  hasRiskOfficerStance: false,
  hasCapitalProtectionScore: false,
  hasMarketAlignmentScore: false,
  hasDisciplineAssessment: false,
  hasFullCounterfactualProofEngine: false,
  hasAuditPdf: false,
  hasPmReadyReporting: false,
  hasComplianceFeatures: false,
  hasAuditApi: false,
  hasMultiUserDashboards: false,
  hasDisciplineReportsDailyWeekly: false,
  hasWhiteLabelAi: false,
  hasCustomRiskOfficerProfiles: false,
  hasPrioritySupport: false,
};

// Free Plan: limited taste of the product — 5 analyses, basic verdict + journal.
// This is the permanent floor for any logged-in user who isn't paying/trialing.
const freeFlags: FeatureFlags = {
  ...noAccessFlags,
  aiAnalysesLimit: 5,
  canUseAiAnalysis: true,
  canUseJournal: true,
  canSeeBasicNoTrade: true,
};

// Starter Plan ($0 / free forever): matches planConfig.ts + subscription_plans
const starterFlags: FeatureFlags = {
  ...noAccessFlags,
  aiAnalysesLimit: 5,
  canUseAiAnalysis: true,
  canUseJournal: true,
  canSeeBasicNoTrade: true,
};

// Pro Trader Plan: Unlimited + core institutional logic
const proTraderFlags: FeatureFlags = {
  ...starterFlags,
  aiAnalysesLimit: null, // unlimited
  hasStructuredNoTradeLogic: true,
  hasCounterfactualTracking: true,
  hasDecisionQualityScore: true,
  hasRiskAvoidanceScore: true,
  hasCapitalPreservationPercent: true,
  hasImpulsiveTraderBenchmark: true,
  hasTdiScore: true,
  tdiVersion: 'base',
};

// Institutional AI: Full governance + audit layer
const institutionalAiFlags: FeatureFlags = {
  ...proTraderFlags,
  tdiVersion: 'premium_850',
  hasTdiHistoryChart: true,
  hasBehavioralDriftDetection: true,
  hasOvertradingClusterAnalysis: true,
  hasInstitutionalReviewBlock: true,
  hasRiskOfficerStance: true,
  hasCapitalProtectionScore: true,
  hasMarketAlignmentScore: true,
  hasDisciplineAssessment: true,
  hasFullCounterfactualProofEngine: true,
  hasAuditPdf: true,
  hasPmReadyReporting: true,
};

// Enterprise: Everything unlocked
const enterpriseFlags: FeatureFlags = {
  ...institutionalAiFlags,
  hasComplianceFeatures: true,
  hasAuditApi: true,
  hasMultiUserDashboards: true,
  hasDisciplineReportsDailyWeekly: true,
  hasWhiteLabelAi: true,
  hasCustomRiskOfficerProfiles: true,
  hasPrioritySupport: true,
};

// Plan ID to flags mapping
const planFlagsMap: Record<string, FeatureFlags> = {
  'none': noAccessFlags,
  'free': freeFlags,
  'starter': starterFlags,
  'pro_trader': proTraderFlags,
  'trial_pro_trader': proTraderFlags, // Trial gets Pro Trader features
  'institutional_ai': institutionalAiFlags,
  'enterprise': enterpriseFlags,
};

export interface UserForFlags {
  subscription_plan_id?: string | null;
  analyses_used_this_month?: number;
  role?: string;
  is_in_trial?: boolean;
  trial_ends_at?: string | null;
  paid_until?: string | null;
  billing_status?: string | null;
  billing_type?: string | null; // 'monthly' | 'annual' | 'lifetime'
}

/**
 * Check if user has active paid subscription (bypasses trial)
 * Priority: Lifetime > Active billing > Trial
 */
export function hasActivePaidSubscription(user: UserForFlags | null): boolean {
  if (!user) return false;
  
  // PRIORITY 1: Lifetime users ALWAYS have access (check this FIRST)
  if (user.billing_type === 'lifetime') {
    console.log('✅ Lifetime user detected - full access granted');
    return true;
  }
  
  // PRIORITY 2: Active billing status with valid subscription
  if (user.billing_status === 'active' && user.subscription_plan_id) {
    // Check paid_until if set
    if (user.paid_until) {
      const paidUntil = new Date(user.paid_until);
      if (paidUntil > new Date()) {
        console.log('✅ Active paid subscription detected');
        return true;
      }
    } else {
      // Active status without paid_until = assume active (legacy support)
      console.log('✅ Active status without paid_until - assuming active');
      return true;
    }
  }
  
  // PRIORITY 3: Fallback for users with subscription_plan_id but billing fields not yet loaded
  // This handles race conditions where database data is delayed
  if (user.subscription_plan_id && 
      user.subscription_plan_id !== 'none' && 
      user.billing_type === undefined && 
      user.billing_status === undefined) {
    console.log('⚠️ Billing fields not loaded yet, but subscription_plan_id exists - allowing access temporarily');
    return true;
  }
  
  return false;
}

/**
 * Check if user is in an active trial
 * NOTE: Only applies if user does NOT have active paid subscription
 */
export function isInActiveTrial(user: UserForFlags | null): boolean {
  if (!user) return false;
  
  // Paid subscribers are never considered "in trial"
  if (hasActivePaidSubscription(user)) return false;
  
  if (!user.is_in_trial) return false;
  if (!user.trial_ends_at) return false;
  
  const trialEndsAt = new Date(user.trial_ends_at);
  return trialEndsAt > new Date();
}

/**
 * Check if subscription is expired based on paid_until
 */
export function isSubscriptionExpired(user: UserForFlags | null): boolean {
  if (!user) return true;
  
  // Admin never expires
  if (user.role === 'admin') return false;
  
  // Lifetime users never expire
  if (user.billing_type === 'lifetime') return false;
  
  // Check if has active paid subscription
  if (hasActivePaidSubscription(user)) return false;
  
  // Check if in active trial
  if (isInActiveTrial(user)) return false;
  
  // If billing_status is explicitly expired
  if (user.billing_status === 'expired') return true;
  
  // If user has a plan and paid_until, check if expired
  if (user.subscription_plan_id && user.paid_until) {
    const paidUntil = new Date(user.paid_until);
    if (paidUntil < new Date()) return true;
  }
  
  // If user has a plan but no paid_until (legacy), consider active
  // This handles existing users who don't have paid_until set yet
  if (user.subscription_plan_id && !user.paid_until && user.billing_status !== 'expired') {
    return false;
  }
  
  return false;
}

/**
 * Get the effective plan ID considering trial status and expiration
 * Priority: Admin > Lifetime/Active Paid > Trial > Expired
 */
export function getEffectivePlanId(user: UserForFlags | null): string {
  if (!user) return 'none';
  
  // Admin bypass
  if (user.role === 'admin') {
    return user.subscription_plan_id || 'institutional_ai';
  }
  
  // PRIORITY 1: Active paid subscription (Lifetime or billing_status='active')
  // This takes precedence over trial status
  if (hasActivePaidSubscription(user)) {
    return user.subscription_plan_id || 'none';
  }
  
  // PRIORITY 2: Active trial (only if not paid)
  if (isInActiveTrial(user)) {
    return 'trial_pro_trader';
  }
  
  // PRIORITY 3: Check if subscription is expired → fall back to Free (not locked out)
  if (isSubscriptionExpired(user)) {
    return 'free';
  }

  // Floor for any logged-in user: Free (freemium). Never lock out to 'none'.
  return user.subscription_plan_id || 'free';
}

/**
 * Get feature flags for a user based on their subscription plan
 */
export function getFeatureFlagsForUser(user: UserForFlags | null): FeatureFlags {
  if (!user) {
    return noAccessFlags;
  }
  
  const effectivePlan = getEffectivePlanId(user);
  return planFlagsMap[effectivePlan] || noAccessFlags;
}

/**
 * Check if user can perform an AI analysis (within limits)
 */
export function canPerformAiAnalysis(user: UserForFlags | null): { 
  allowed: boolean; 
  reason?: string;
  remaining?: number;
} {
  const flags = getFeatureFlagsForUser(user);
  
  if (!flags.canUseAiAnalysis) {
    return { 
      allowed: false, 
      reason: 'AI Analysis requires a subscription. Please upgrade to Starter or higher.' 
    };
  }
  
  // Check limit for Starter plan
  if (flags.aiAnalysesLimit !== null) {
    const used = user?.analyses_used_this_month || 0;
    const remaining = flags.aiAnalysesLimit - used;
    
    if (remaining <= 0) {
      return { 
        allowed: false, 
        reason: `You've reached your monthly limit of ${flags.aiAnalysesLimit} analyses on the Starter plan. Upgrade to Pro Trader for unlimited analyses.`,
        remaining: 0
      };
    }
    
    return { allowed: true, remaining };
  }
  
  return { allowed: true, remaining: undefined }; // unlimited
}

/**
 * Get plan display info
 */
export function getPlanDisplayInfo(planId: string | null | undefined): {
  name: string;
  tier: string;
  color: string;
} {
  const planInfo: Record<string, { name: string; tier: string; color: string }> = {
    'none': { name: 'No Plan', tier: 'none', color: 'slate' },
    'starter': { name: 'Starter', tier: 'retail', color: 'blue' },
    'pro_trader': { name: 'Pro Trader', tier: 'professional', color: 'violet' },
    'trial_pro_trader': { name: 'Trial (Pro Trader)', tier: 'trial', color: 'cyan' },
    'institutional_ai': { name: 'Institutional AI', tier: 'institutional', color: 'emerald' },
    'enterprise': { name: 'Enterprise', tier: 'enterprise', color: 'amber' },
  };
  
  return planInfo[planId || 'none'] || planInfo['none'];
}

/**
 * Get upgrade message for a specific feature
 */
export function getUpgradeMessage(feature: keyof FeatureFlags): string {
  const featureToMinPlan: Record<string, string> = {
    // Starter features
    canUseAiAnalysis: 'Starter',
    canUseJournal: 'Starter',
    canSeeBasicNoTrade: 'Starter',
    
    // Pro Trader features
    hasStructuredNoTradeLogic: 'Pro Trader',
    hasCounterfactualTracking: 'Pro Trader',
    hasDecisionQualityScore: 'Pro Trader',
    hasRiskAvoidanceScore: 'Pro Trader',
    hasCapitalPreservationPercent: 'Pro Trader',
    hasImpulsiveTraderBenchmark: 'Pro Trader',
    hasTdiScore: 'Pro Trader',
    
    // Institutional AI features
    hasInstitutionalReviewBlock: 'Institutional AI',
    hasRiskOfficerStance: 'Institutional AI',
    hasCapitalProtectionScore: 'Institutional AI',
    hasMarketAlignmentScore: 'Institutional AI',
    hasDisciplineAssessment: 'Institutional AI',
    hasFullCounterfactualProofEngine: 'Institutional AI',
    hasTdiHistoryChart: 'Institutional AI',
    hasBehavioralDriftDetection: 'Institutional AI',
    hasOvertradingClusterAnalysis: 'Institutional AI',
    hasAuditPdf: 'Institutional AI',
    hasPmReadyReporting: 'Institutional AI',
    
    // Enterprise features
    hasComplianceFeatures: 'Enterprise',
    hasAuditApi: 'Enterprise',
    hasMultiUserDashboards: 'Enterprise',
    hasDisciplineReportsDailyWeekly: 'Enterprise',
    hasWhiteLabelAi: 'Enterprise',
    hasCustomRiskOfficerProfiles: 'Enterprise',
    hasPrioritySupport: 'Enterprise',
  };
  
  const minPlan = featureToMinPlan[feature] || 'a higher';
  return `This feature requires the ${minPlan} plan. Upgrade to unlock it.`;
}

/**
 * Calculate days remaining in trial
 */
export function getTrialDaysRemaining(user: UserForFlags | null): number | null {
  if (!user || !user.is_in_trial || !user.trial_ends_at) return null;
  
  const trialEndsAt = new Date(user.trial_ends_at);
  const now = new Date();
  
  if (trialEndsAt <= now) return 0;
  
  const diffMs = trialEndsAt.getTime() - now.getTime();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

export { noAccessFlags, starterFlags, proTraderFlags, institutionalAiFlags, enterpriseFlags };
